/* istanbul ignore file */

import React from 'react';
import { sendGAEvent } from '@noths/polaris-client-google-analytics';
import { useVisibilityObserver } from '@noths/polaris-client-react-hooks';
import type { ClickCarouselLinkPayload } from '@noths/polaris-client-ribbons-design-system';
import { PillBarCarousel } from '@noths/polaris-client-ribbons-design-system';
import type { Link } from '@noths/polaris-dev-ts-types';

import type { FilterUid } from 'src/components/organisms/Filter/constants/uids';
import * as styles from 'src/components/organisms/Filter/styles/QuickFilters.styles';
import type { FilterOptionValue, SelectedFilterOption } from 'src/types';
import { EventCategory } from 'src/types';

const QUICK_FILTERS_VISIBILITY_THRESHOLD = 0.7;

const addOrRemove = <T,>(array: T[], item: T) =>
  array.includes(item) ? array.filter((c) => c !== item) : [...array, item];

export interface QuickFiltersProps {
  filterUid: FilterUid;
  filterLinks?: Link[];
  selectedFilterOptions?: string | number | FilterOptionValue[];
  onSelectFilterOption?: (selectedFilterOption: SelectedFilterOption) => void;
}

export const QuickFilters = ({
  filterUid,
  filterLinks = [],
  selectedFilterOptions = [],
  onSelectFilterOption,
}: QuickFiltersProps) => {
  const { observedElementRef: quickFiltersRef } = useVisibilityObserver(
    () => {
      sendGAEvent({
        event: 'custom_event',
        event_category: 'Quick filters',
        event_action: 'quick filter impression',
        event_label: 'delivery',
      });
    },
    { observerOptions: { threshold: QUICK_FILTERS_VISIBILITY_THRESHOLD } },
  );

  const links = filterLinks.map((link) => {
    return {
      href: '',
      text: link.title!,
      active: Boolean(
        Array.isArray(selectedFilterOptions)
          ? selectedFilterOptions.find((selectedFilter) => selectedFilter === link.href)
          : selectedFilterOptions === link.href,
      ),
    };
  });

  const handleLinkClick = (
    evt: React.MouseEvent<HTMLAnchorElement>,
    { index }: ClickCarouselLinkPayload,
  ) => {
    evt.preventDefault();
    if (!filterLinks || !filterLinks[index]) {
      return;
    }

    const optionValue = filterLinks[index].href!;
    const filterOptionValue = Array.isArray(selectedFilterOptions)
      ? addOrRemove(selectedFilterOptions, optionValue)
      : optionValue;

    onSelectFilterOption?.({
      filterUid,
      filterOptionValue,
      eventCategory: EventCategory.QuickFilters,
    });
  };

  if (!filterLinks) {
    return null;
  }

  return (
    <div css={styles.quickFilters} ref={quickFiltersRef}>
      <PillBarCarousel title="Quick filters" links={links} onLinkClick={handleLinkClick} />
    </div>
  );
};
